import { ButtonGroup, ButtonText, Col, Form, FormControl, Icon, Input, InputOption, Row, Typography } from '@visa/vds';
import { Checkbox, Modal, Table } from 'antd';
import MCSGridRevert from 'components/MCSGridRevert';
import { GlobalAlertContext } from 'context/GlobalAlertContext';
import React, { useContext, useEffect, useState } from 'react';
import { EmailNotificationApi } from 'services/emailNotification.service';
import "./VSSClientModal.css";
import { usePromiseTracker } from 'react-promise-tracker';
import MCSOverlayLoading from 'components/MCSOverlayLoading';

const VSSClientModal = ({ isVisible, handleOk, allClients, eventId, volUserId, tableData }) => {
    const [selectedClients, setSelectedClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState(allClients);
    const [insertData, setInsertData] = useState([]);
    const [toDeleteData, setToDeleteData] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);
    const { promiseInProgress } = usePromiseTracker();
    const alertContext = useContext(GlobalAlertContext);

    const isAllSelected =
        selectedClients.length > 0
        && filteredClients.length > 0
        && selectedClients.length === filteredClients.length
        && filteredClients.every(client => selectedClients.includes(client));

    const [sortConfig, setSortConfig] = useState({ order: 'ascend', columnKey: 'BID' });

    const handleSelectAllChange = (e) => {
        const checked = e.target.checked;
        const updatedClients = filteredClients.map(client => ({ ...client, HasSelectedVSSClients: checked }));

        setFilteredClients(updatedClients);

        if (checked) {
            setSelectedClients(updatedClients);

            const newInsertData = [];
            const retainedDeleteData = toDeleteData.filter(client => {
                if (filteredClients.find(item => item.BID === client.BID)) {
                    return false;
                }
                return true;
            });

            filteredClients.forEach(client => {
                if (!tableData.find(item => item.BID === client.BID) &&
                    !insertData.find(item => item.BID === client.BID)) {
                    newInsertData.push({ ...client, HasSelectedVSSClients: true });
                }
            });

            setInsertData([...insertData, ...newInsertData]);
            setToDeleteData(retainedDeleteData);

        } else {
            setSelectedClients([]);

            const newInsertData = insertData.filter(client =>
                !filteredClients.some(item => item.BID === client.BID)
            );

            const newDeleteData = [];
            filteredClients.forEach(client => {
                if (tableData.find(item => item.BID === client.BID) &&
                    !toDeleteData.find(item => item.BID === client.BID)) {
                    newDeleteData.push(client);
                }
            });

            setInsertData(newInsertData);
            setToDeleteData([...toDeleteData, ...newDeleteData]);
        }
    };
    const updateDataToBeInserted = (record) => {
        if (!tableData.find(client => client.BID === record.BID) && !insertData.find(client => client.BID === record.BID)) {
            setInsertData([...insertData, { ...record, HasSelectedVSSClients: true }]);
        }
    };

    const removeDataFromInsert = (record) => {
        setInsertData(insertData.filter(client => client.BID !== record.BID));
    };

    const updateDataToBeDeleted = (record) => {
        if (tableData.find(client => client.BID === record.BID) && !toDeleteData.find(client => client.BID === record.BID)) {
            setToDeleteData([...toDeleteData, record]);
        }
    };

    const removeDataFromDelete = (record) => {
        setToDeleteData(toDeleteData.filter(client => client.BID !== record.BID));
    };

    const handleCheckboxChange = (e, record) => {
        const updatedClients = filteredClients.map(client => {
            if (client.BID === record.BID) {
                return { ...client, HasSelectedVSSClients: e.target.checked };
            }
            return client;
        });

        setFilteredClients(updatedClients);

        if (e.target.checked) {
            setSelectedClients([...selectedClients, { ...record, HasSelectedVSSClients: true }]);
            if (!tableData.find(client => client.BID === record.BID)) {
                updateDataToBeInserted(record);
            } else {
                removeDataFromDelete(record);
            }
        } else {
            setSelectedClients(selectedClients.filter(client => client.BID !== record.BID));
            if (!tableData.find(client => client.BID === record.BID)) {
                removeDataFromInsert(record);
            } else {
                updateDataToBeDeleted(record);
            }
        }
    };

    useEffect(() => {
        setFilteredClients(allClients);
        const initiallySelectedClients = allClients.filter(client => client.HasSelectedVSSClients === true);
        setSelectedClients(initiallySelectedClients);
    }, [allClients]);

    const handleSubmit = async () => {
        setLoading(true);

        try {
            const insertRequestDto = {
                volUserId: volUserId,
                eventId: parseInt(eventId),
                bids: insertData.map(client => client.BID)
            };

            const deleteRequestDto = {
                volUserId: volUserId,
                eventId: parseInt(eventId),
                bids: toDeleteData.map(client => client.BID)
            };

            const promises = [];
            if (insertRequestDto.bids.length > 0) {
                promises.push(EmailNotificationApi.SaveSelectedVssClientsEmailNotification(insertRequestDto));
            }
            if (deleteRequestDto.bids.length > 0) {
                promises.push(EmailNotificationApi.DeleteSelectedVssClientsEmailNotification(deleteRequestDto));
            }

            await Promise.all(promises);
            handleOk();
        } catch (error) {
            alertContext.setErrorMessage("An Error occurred when submitting request");
        } finally {
            setLoading(false);
            setSearchValue('');
            setSortConfig({ order: 'ascend', columnKey: 'BID' });
            setInsertData([]);
            setToDeleteData([]);
        }
    };
    const handleAutoSearch = (value) => {
        setSearchValue(value);
        const filteredData = allClients.filter(client =>
            Object.keys(client)
                .filter(key => key.toLowerCase() !== 'id')
                .some(key => {
                    const clientValue = String(client[key]).toLowerCase();
                    return clientValue.includes(value.toLowerCase());
                })
        ).map(client => {
            const selected = selectedClients.find(sc => sc.BID === client.BID);
            return selected ? { ...client, HasSelectedVSSClients: true } : client;
        });
        setFilteredClients(filteredData);
    };

    const handleManualSearch = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAutoSearch(e.target.value);
        }
    };

    const MODAL_INNER_COLUMNS = [
        {
            title: <Checkbox checked={isAllSelected} onChange={handleSelectAllChange} />,
            dataIndex: 'HasSelectedVSSClients',
            key: 'HasSelectedVSSClients',
            render: (_, record) => (
                <Checkbox
                    checked={selectedClients.some(client => client.BID === record.BID)}
                    onChange={(e) => handleCheckboxChange(e, record)}
                />
            ),
        },
        {
            title: 'BID',
            dataIndex: 'BID',
            key: 'BID',
            sorter: (a, b) => a.BID - b.BID,
            sortOrder: sortConfig.columnKey === 'BID' && sortConfig.order,
        },
        {
            title: 'Company',
            dataIndex: 'LicensedCompanyName',
            key: 'LicensedCompanyName',
            sorter: (a, b) => a.LicensedCompanyName.localeCompare(b.LicensedCompanyName),
            sortOrder: sortConfig.columnKey === 'LicensedCompanyName' && sortConfig.order,
        },
        {
            title: 'Country',
            dataIndex: 'LicensedCountry',
            key: 'LicensedCountry',
            sorter: (a, b) => a.LicensedCountry.localeCompare(b.LicensedCountry),
            sortOrder: sortConfig.columnKey === 'LicensedCountry' && sortConfig.order,
        },
        {
            title: 'Region',
            dataIndex: 'Region',
            key: 'Region',
            sorter: (a, b) => a.Region.localeCompare(b.Region),
            sortOrder: sortConfig.columnKey === 'Region' && sortConfig.order,
        }
    ];

    const handleCancel = () => {
        setSearchValue('');
        setSortConfig({ order: 'ascend', columnKey: 'BID' });
        setInsertData([]);
        setToDeleteData([]);
        handleOk();
    };

    const handleTableChange = (pagination, filters, sorter) => {
        setSortConfig(sorter);
    };

    return (
        <>
            {(promiseInProgress || loading) ? <MCSOverlayLoading /> :
                <Modal
                    title=""
                    visible={isVisible}
                    onOk={handleSubmit}
                    onCancel={handleCancel}
                    width={1000}
                    destroyOnClose
                    footer={
                        <ButtonGroup direction="right">
                            <ButtonText colorScheme="tertiary" onClick={handleCancel}>Cancel</ButtonText>
                            <ButtonText onClick={handleSubmit}>Save</ButtonText>
                        </ButtonGroup>
                    }>
                    <MCSGridRevert className="modal-grid">
                        <Row>
                            <Col>
                                <Typography className="modal-title" tag="h1" variant="headline">
                                    List of VSS Clients
                                </Typography>
                            </Col>
                            <Col>
                                <Form>
                                    <FormControl position="right">
                                        <Input
                                            contentBeforeInput={
                                                <InputOption>
                                                    <Icon
                                                        name="search"
                                                        resolution="low"
                                                        className="search-icon"
                                                    />
                                                </InputOption>
                                            }
                                            aria-label="Site-wide"
                                            useNativePlaceholder
                                            name="search"
                                            placeholder="Search BID/Company/Country/Region"
                                            value={searchValue}
                                            showHelpText
                                            onKeyPress={handleManualSearch}
                                            onChange={(e) => handleAutoSearch(e.target.value)}
                                        />
                                    </FormControl>
                                </Form>
                            </Col>
                        </Row>
                        <Row className="table-row">
                            <Table
                                dataSource={filteredClients}
                                columns={MODAL_INNER_COLUMNS}
                                onChange={handleTableChange}
                                pagination={{ position: ['bottomCenter'] }}
                                rowKey="BID"
                                className="modal-table"
                            />
                        </Row>
                    </MCSGridRevert>
                </Modal>}
        </>
    );
};

export default VSSClientModal;